"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GenerateService = exports.DEFAULT_LINE_STYLE = void 0;
const CotConstant_1 = require("../cot/CotConstant");
const signals_react_1 = require("@preact/signals-react");
const is_blank_1 = __importDefault(require("@sedan-utils/is-blank"));
const lodash_1 = require("lodash");
exports.DEFAULT_LINE_STYLE = {
    strokeWidth: 2,
    stroke: "#B0B0B7",
};
class CotConstants {
}
CotConstants.blueprintPreviewStyle = {
    opacity: 0.3,
};
CotConstants.blueprintPreviewNoMatchedStyle = {
    opacity: 0.8,
};
class BlocState {
    constructor() {
        this.nodeDataRef = { current: {} };
        this.newLineDataRef = { current: {} };
        this.nodeIndexRef = { current: 0 };
        this.edgeIndexRef = { current: 0 };
        this.nodes = [];
        this.edges = [];
        this.relatedNodes = (0, signals_react_1.signal)([]);
        this.relateEdges = (0, signals_react_1.signal)([]);
    }
}
class GenerateService {
    constructor() {
        this.state = new BlocState();
    }
    init(params) {
        this.state.nodeDataRef = params.nodeDataRef;
        this.state.newLineDataRef = params.newLineDataRef;
        this.state.edgeIndexRef = params.edgeIndexRef;
        this.state.nodeIndexRef = params.nodeIndexRef;
        this.state.nodes = (0, lodash_1.cloneDeep)(params.nodes);
        this.state.edges = (0, lodash_1.cloneDeep)(params.edges);
    }
    previewBlueprint(params) {
        const { id, inputs, outputs, ...rest } = params;
        this.init(rest);
        this.saveRestoreData(rest);
        const nodesData = this.getNodesData(id);
        if (nodesData === null) {
            return null;
        }
        const { sourceNode, targetNode, sourceNodes } = nodesData;
        targetNode.data.displayType = "blueprint";
        targetNode.data.textAreaValue = outputs;
        targetNode.data.blueprintPreviewStyle = CotConstants.blueprintPreviewStyle;
        const matched = this.matchInputs({
            inputs,
            sourceNodes,
        });
        (0, lodash_1.forEach)(sourceNodes, node => {
            if (matched.matchedSourceNodeIds.includes(node.id)) {
                node.data.blueprintPreviewStyle = CotConstants.blueprintPreviewStyle;
            }
            else {
                node.data.blueprintPreviewStyle = CotConstants.blueprintPreviewNoMatchedStyle;
            }
        });
        return this.createReactFlowNodeEdges({
            id,
            ...matched,
            sourceNode,
            blueprintPreviewStyle: CotConstants.blueprintPreviewStyle,
        });
    }
    generateInputs(params) {
        const { id, inputs, ...rest } = params;
        this.init(rest);
        const sources = this.getNodesData(id);
        if (sources === null) {
            return null;
        }
        const { sourceNode, sourceNodes } = sources;
        const matched = this.matchInputs({
            inputs,
            sourceNodes,
        });
        return this.createReactFlowNodeEdges({
            id,
            ...matched,
            sourceNode,
        });
    }
    previewRestore() {
        return {
            nodes: this.state.relatedNodes.value,
            edges: this.state.relateEdges.value,
        };
    }
    getNodesData(id) {
        const nodes = this.state.nodes;
        const node = (0, lodash_1.find)(nodes, node => node.id === id);
        if (!node) {
            return null;
        }
        const targetNodeId = node?.data.targetNodeId;
        const targetNode = (0, lodash_1.find)(nodes, node => node.id === targetNodeId);
        if (!targetNode) {
            return null;
        }
        const sourceNodeIds = (0, lodash_1.map)(node?.data.flows, flow => {
            return flow.sourceNodeId;
        });
        const sourceNodes = (0, lodash_1.filter)(nodes, node => sourceNodeIds.includes(node.id));
        const sourceNodeId = sourceNodeIds.pop();
        const sourceNode = (0, lodash_1.find)(nodes, node => node.id === sourceNodeId);
        return {
            sourceNode,
            targetNode,
            sourceNodes,
        };
    }
    matchInputs(params) {
        const matchedSourceNodeIds = [];
        const matchedSourceInputs = [];
        const needCreateNodeInputs = [];
        (0, lodash_1.forEach)(params.inputs, input => {
            const matchedNode = (0, lodash_1.chain)(params.sourceNodes)
                .filter(node => matchedSourceNodeIds.includes(node.id) === false)
                .find(node => node.data.inputTitle === input.name || (0, is_blank_1.default)(node.data.inputTitle))
                .value();
            if (matchedNode) {
                matchedSourceNodeIds.push(matchedNode.id);
                matchedSourceInputs.push(input);
            }
            else {
                needCreateNodeInputs.push(input);
            }
        });
        return { matchedSourceNodeIds, matchedSourceInputs, needCreateNodeInputs };
    }
    createReactFlowNodeEdges(params) {
        const nodes = this.state.nodes;
        const node = (0, lodash_1.find)(nodes, node => node.id === params.id);
        const newNodes = (0, lodash_1.map)(params.needCreateNodeInputs, (input, index) => this.createNode(input, params.sourceNode.position, index, params.blueprintPreviewStyle));
        const newLines = (0, lodash_1.map)(newNodes, node => this.createLine({ sourceId: node.id, targetId: params.id, isSource: true }));
        const newFlows = (0, lodash_1.map)(newLines, line => this.createFlows(line));
        node.data.flows = [...node?.data.flows, ...newFlows];
        // change matched nodes generated title
        params.matchedSourceNodeIds.forEach((id, index) => {
            const node = (0, lodash_1.find)(nodes, node => node.id === id);
            node.data.generatedLabel = params.matchedSourceInputs[index].name;
        });
        return {
            nodes: [...this.state.nodes, ...newNodes],
            edges: [...this.state.edges, ...newLines],
        };
    }
    createNode(input, position, index, blueprintPreviewStyle) {
        const id = `editor-${this.state.nodeIndexRef.current++}`;
        return {
            id: id,
            type: "customNode",
            data: {
                ...this.state.nodeDataRef.current,
                blueprintPreviewStyle,
                displayType: input.type,
                inputTitle: input.name,
                placeholder: input.description,
            },
            position: {
                x: position.x,
                y: position.y + 300 * (index + 1),
            },
        };
    }
    createLine(params) {
        const id = `new-line-${this.state.edgeIndexRef.current++}`;
        return {
            id,
            source: params.sourceId,
            target: params.targetId,
            sourceHandle: "b",
            targetHandle: "a",
            type: "customNewLine",
            animated: false,
            markerEnd: params.isSource ? CotConstant_1.CotConstant.transparentMarkerEnd : CotConstant_1.CotConstant.defaultMarkerEnd,
            style: exports.DEFAULT_LINE_STYLE,
            data: this.state.newLineDataRef.current,
        };
    }
    createFlows(edge) {
        return {
            creationMethod: false,
            sourceLineId: edge.id,
            sourceNodeId: edge.source,
        };
    }
    saveRestoreData(params) {
        this.state.relateEdges.value = params.edges;
        this.state.relatedNodes.value = params.nodes;
    }
}
exports.GenerateService = GenerateService;
